import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './App.css';

import FrameworkManager from './FrameworkManager';

const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();
const USER_STORAGE_KEY = "matcha_auth_user";
type AuthUser = { email: string; name: string; picture: string };
type CalendarValue = [Date, Date] | null;
type CalendarBlock = { start: string; end: string; url: string; status?: 'created' | 'done' };

function App() {
  const [selectedDates, setSelectedDates] = useState<CalendarValue>(null);
  const [loading, setLoading] = useState(false);
  const [sheetUrl, setSheetUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calendarBlocks, setCalendarBlocks] = useState<CalendarBlock[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [selectionStart, setSelectionStart] = useState<Date | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [logVisible, setLogVisible] = useState<boolean>(false);
  
  const [user, setUser] = useState<AuthUser | null>(null);

  const [isLogPolling, setIsLogPolling] = useState(false);
  const logIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const showManualLogBanner = logVisible && !isLogPolling;

  const stopLogPolling = () => {
    if (logIntervalRef.current) {
      clearInterval(logIntervalRef.current);
      logIntervalRef.current = null;
    }
    setIsLogPolling(false);
  };

  const stopStatusPolling = () => {
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }
  };

  const startLogPolling = () => {
    stopLogPolling();
    const id = setInterval(async () => {
      try {
        const response = await authedFetch('/api/logs');
        const data = await response.text();
        setLogLines(data.split('\n').slice(-50));
      } catch {
        setLogLines(['⚠️ Failed to fetch logs']);
      }
    }, 2000);
    logIntervalRef.current = id;
    setIsLogPolling(true);
  };

  const startStatusPolling = () => {
    stopStatusPolling();
    const id = setInterval(async () => {
      try {
        const res = await authedFetch('/api/pipeline-status');
        const data = await res.json();
        if (data.status === 'success') {
          setSheetUrl(data.sheet_url);
          refreshCalendarBlocks();
          stopLogPolling();
          stopStatusPolling();
          setLoading(false);
        } else if (data.status === 'error') {
          setError(data.detail || 'Pipeline failed');
          stopLogPolling();
          stopStatusPolling();
          setLoading(false);
        } else if (data.status === 'killed') {
          stopLogPolling();
          stopStatusPolling();
          setLoading(false);
        }
      } catch {
        // ignore transient network errors
      }
    }, 3000);
    statusIntervalRef.current = id;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/pipeline-status');
        const data = await res.json();
        if (data.status === 'running') {
          setLoading(true);
          startLogPolling();
          startStatusPolling();
        }
      } catch {
        // ignore — backend may not be up yet
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    }
  }, []);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: credentialResponse.credential }),
      });
      if (!response.ok) {
        const err = await response.json();
        setToast('❌ ' + (err.error || 'Sign-in failed'));
        return;
      }
      const authUser: AuthUser = await response.json();
      setUser(authUser);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(authUser));
    } catch {
      setToast('❌ Sign-in failed');
    }
  };

  const signOut = useCallback(async () => {
    try { await fetch('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
    setUser(null);
    localStorage.removeItem(USER_STORAGE_KEY);
  }, []);

  const logout = () => { signOut(); };

  const authedFetch = useCallback(async (url: string, options?: RequestInit): Promise<Response> => {
    const res = await fetch(url, options);
    if (res.status === 401) { await signOut(); throw new Error('Session expired. Please sign in again.'); }
    return res;
  }, [signOut]);

  const refreshCalendarBlocks = useCallback(async () => {
    try {
      console.log("🌍 Fetching fresh calendar blocks from server...");
      const response = await authedFetch('/api/calendar-blocks?' + Date.now());
      const text = await response.text();

      try {
        const blocks = JSON.parse(text);
        console.log("📦 Raw fetched blocks from server:", blocks);

        const cleanedBlocks = blocks.map((b: CalendarBlock) => ({
          ...b,
          status: b.status ?? 'done',
        }));

        setCalendarBlocks(cleanedBlocks);
      } catch (parseError) {
        console.error("❌ Failed to parse calendar blocks JSON:", parseError);
        console.error("🔴 Server returned:", text);
        setToast("Failed to load calendar blocks (invalid response)");
      }
    } catch (error) {
      console.error('Failed to load calendar blocks:', error);
      setToast('⚠️ Failed to refresh calendar data');
    }
  }, [authedFetch]);

  useEffect(() => {
    const handleMouseUp = () => {};
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  useEffect(() => {
    refreshCalendarBlocks();
  }, [refreshCalendarBlocks]);

  useEffect(() => {
    const logContainer = document.getElementById('log-container');
    if (logContainer) {
      logContainer.scrollTop = logContainer.scrollHeight;
    }
  }, [logLines]);

  function generateDateRange(start: Date, end: Date): Date[] {
    let range: Date[] = [];
    let current = new Date(start);
    let endDate = new Date(end);
    if (current > endDate) [current, endDate] = [endDate, current];
    while (current <= endDate) {
      range.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return range;
  }

  const blockMap = new Map<string, CalendarBlock>();
  const now = new Date();
  calendarBlocks.forEach((block: CalendarBlock) => {
    const blockStatus = block.status ?? 'done';
    const range = generateDateRange(new Date(block.start), new Date(block.end));
    console.log("📅 Block", block.start, "to", block.end, "→ status:", block.status, "→ range:", range.map(d => d.toDateString()));
    if (new Date(block.start) > now) return;
    range.forEach(date => {
      const key = date.toDateString();
      if (!blockMap.has(key)) {
        blockMap.set(key, { ...block, status: blockStatus });
      }
    });
  });

  const onTileClick = (date: Date) => {
    const block = blockMap.get(date.toDateString());
    if (block) {
      window.open(block.url, '_blank');
    } else {
      if (!selectionStart) {
        setSelectionStart(date);
        setSelectedDates([date, date]);
      } else {
        setSelectedDates([selectionStart, date]);
        setSelectionStart(null);
      }
    }
  };

  const runPipeline = async () => {
    if (!selectedDates || selectedDates.length !== 2 || !selectedDates[0] || !selectedDates[1]) {
      alert("Please select a valid date range first.");
      return;
    }

    const [start, end] = selectedDates;
    const pad = (n: number) => String(n).padStart(2, '0');
    const startDate = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    const endDate = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;

    setLoading(true);
    setError(null);
    setSheetUrl(null);

    try {
      const response = await authedFetch('/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });
      const data = await response.json();

      if (data.status === 'already_running') {
        setError('A pipeline is already running.');
        setLoading(false);
        return;
      }

      if (data.status !== 'started') {
        setError('Failed to start pipeline: ' + JSON.stringify(data));
        setLoading(false);
        return;
      }

      startLogPolling();
      startStatusPolling();
    } catch (error: any) {
      setError('Pipeline request failed: ' + error.message);
      setLoading(false);
    }
  };

  const markBlockAsDone = async (date: Date) => {
    const block = blockMap.get(date.toDateString());
    if (!block || block.status === 'done') return;

    try {
      const response = await authedFetch('/api/mark-done', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start: block.start, end: block.end, url: block.url }),
      });
      const result = await response.json();

      console.log("🔁 markBlockAsDone response:", result);

      if (result.status === 'success') {
        const updated = await authedFetch('/api/calendar-blocks?' + Date.now());
        const newBlocks = await updated.json();
        setCalendarBlocks(newBlocks);
        setToast(`✅ Marked ${block.start}–${block.end} as done`);
      } else {
        setToast('⚠️ Failed to mark as done: ' + result.detail);
      }
    } catch (err) {
      console.error('❌ Error connecting to server:', err);
      setToast('⚠️ Error connecting to server');
    } finally {
      setTimeout(() => setToast(null), 3000);
    }
  };

  const killPipeline = async () => {
    try {
      const response = await authedFetch("/api/abort", { method: "POST" });
      const result = await response.json();

      let message = "";
      if (result.status === 'killed') {
        message = "🛑 Pipeline terminated";
      } else if (result.status === 'not_running') {
        message = "ℹ️ No pipeline process to kill";
      } else {
        message = "⚠️ Unknown response from kill switch";
      }

      setToast(message);
      setLogVisible(true);
      setLogLines(prev => [...prev, message]);
    } catch (err) {
      const failMsg = "❌ Failed to send kill signal";
      setToast(failMsg);
      setLogVisible(true);
      setLogLines(prev => [...prev, failMsg]);
    } finally {
      setTimeout(() => setToast(null), 3000);
    }
  };

  return (
    <>
      {!user ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setToast('❌ Sign-in failed')} />
          </GoogleOAuthProvider>
        </div>
      ) : (
        <>
          <Router>
            <div className="App" style={{ width: '100%' }}>
              {/* Centered Title and Subtitle */}
              <div style={{ textAlign: 'center', marginTop: '2em' }}>
                <h1>🍵 Matcha</h1>
                <h3 style={{ marginTop: 0, color: '#555', fontWeight: 400 }}>Call-off Matching Pipeline</h3>
              </div>

              {/* Three columns: Calendar, Buttons, Logs */}
              <div
                style={{
                  display: 'flex',
                  gap: '2em',
                  padding: '2em',
                  alignItems: 'flex-start',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {/* Calendar column */}
                <div style={{ flex: 1, minWidth: '420px' }}>
              <Calendar
                selectRange={true}
                value={selectedDates ?? undefined}
                onClickDay={(date: Date) => {
                  const block = blockMap.get(date.toDateString());

                  if (block?.status === 'done') {
                    onTileClick(date); // ✅ open green sheet
                  } else if (!block) {
                    if (!selectionStart) {
                      setSelectionStart(date);
                      setSelectedDates([date, date]);
                    } else {
                      setSelectedDates([selectionStart, date]);
                      setSelectionStart(null);
                    }
                  }
                  // ✅ Do nothing if block is yellow (created)
                }}
                onChange={(range) => {
                  const clickedDate = Array.isArray(range) ? range[0] : range;
                  if (!clickedDate) return;

                  const block = blockMap.get(clickedDate.toDateString());

                  if (block?.status === 'done') {
                    onTileClick(clickedDate);
                    return;
                  }

                  if (block?.status === 'created') {
                    return;
                  }

                  if (!block && Array.isArray(range) && range.length === 2 && range[0] instanceof Date && range[1] instanceof Date) {
                    setSelectedDates([range[0], range[1]]);
                  }
                }}
                tileContent={({ date, view }) => {
                  if (view !== 'month') return null;
                  const dateKey = date.toDateString();
                  const block = blockMap.get(dateKey);
                  if (!block) return null;
                  const dateStr = date.toISOString().split('T')[0];
                  const status = block.status ?? 'done';
                  const isStart = dateStr === block.start;
                  const isEnd = dateStr === block.end;
                  return (
                    <div
                      className={`calendar-block-span ${status === 'done' ? 'block-done' : 'block-created'} ${isStart ? 'block-start' : ''} ${isEnd ? 'block-end' : ''}`}
                      style={{
                        position: 'absolute',
                        inset: 2,
                        zIndex: status === 'done' ? 0 : 4,
                        pointerEvents: status === 'done' ? 'none' : 'auto',
                        borderRadius: isStart ? '10px 0 0 10px' : isEnd ? '0 10px 10px 0' : undefined,
                        backgroundColor: status === 'done' ? '#c8e6c9' : undefined,
                        opacity: 0.8,
                      }}
                      title={status === 'created' ? 'Click to mark as done' : undefined}
                    >
                      {status === 'created' && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            markBlockAsDone(date);
                          }}
                          style={{
                            backgroundColor: '#333',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            zIndex: 5,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            pointerEvents: 'auto'
                          }}
                          className="hover-mark-done"
                        >
                          Mark as Done
                        </div>
                      )}
                    </div>
                  );
                }}
              />
                </div>
                {/* Buttons column */}
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    gap: '0.7em',
                    maxWidth: 340,
                    minWidth: 220,
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7em', margin: '0 0 0 0' }}>
                    <Link
                      to="/frameworks"
                      style={{
                        textDecoration: 'none',
                        padding: '6px 8px',
                        backgroundColor: '#ddd',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontWeight: 500,
                        color: '#222',
                        fontSize: '0.97em'
                      }}
                    >
                      ⚙️ Framework Manager
                    </Link>
                    <a
                      href="https://docs.google.com/spreadsheets/d/1YfwgdiXWW7R1kGYnHMZtgcZLaBySC_kJupGbTEs_uto/edit"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: '#444',
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        fontWeight: 500,
                        fontSize: '0.97em'
                      }}
                    >
                      Blocks
                    </a>
                  </div>
                  <button
                    onClick={runPipeline}
                    disabled={loading}
                    style={{
                      width: '100%',
                      marginBottom: '0.5em',
                      padding: '7px 0',
                      fontWeight: 600,
                      fontSize: '0.97em'
                    }}
                  >
                    {loading ? 'Running...' : 'Run Pipeline'}
                  </button>
                  {loading && (
                    <button
                      onClick={killPipeline}
                      style={{
                        width: '100%',
                        background: 'red',
                        color: 'white',
                        marginBottom: '0.5em',
                        padding: '7px 0',
                        fontWeight: 600,
                        fontSize: '0.97em'
                      }}
                    >
                      Kill Pipeline
                    </button>
                  )}
                  <button
                    onClick={logout}
                    style={{
                      marginTop: '0.5em',
                      width: '100%',
                      padding: '7px 0',
                      fontSize: '0.97em'
                    }}
                  >
                    Sign out
                  </button>
                  {sheetUrl && (
                    <div style={{ marginTop: '1em', textAlign: 'center' }}>
                      <span role="img" aria-label="success">✅</span>{' '}
                      <a href={sheetUrl} target="_blank" rel="noopener noreferrer">Open Google Sheet</a>
                    </div>
                  )}
                  {error && (
                    <div style={{ color: 'red', marginTop: '1em', textAlign: 'center' }}>
                      ⚠️ {error}
                    </div>
                  )}
                </div>
                {/* Logs column */}
                <div
                  style={{
                    flex: 1,
                    maxHeight: '65vh',
                    overflowY: 'auto',
                    position: 'sticky',
                    top: '2em',
                    backgroundColor: '#f8f8f8',
                    padding: '12px',
                    border: '1px solid #bbb',
                    borderRadius: '8px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-line',
                    minWidth: '320px',
                    minHeight: '300px'
                  }}
                >
                  {showManualLogBanner && (
                    <div style={{
                      padding: '6px 8px',
                      backgroundColor: '#fff9e6',
                      border: '1px solid #ffcc00',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      fontSize: '12px'
                    }}>
                      Log polling is paused. These logs may be outdated.
                    </div>
                  )}                               
                  {logLines.length === 0 || (logLines.length === 1 && !logLines[0]) ? (
                    <div style={{ color: '#bbb', textAlign: 'center', marginTop: '5em' }}>
                      Logs will appear here...
                    </div>
                  ) : (
                    logLines.map((line, i) => (
                      <div
                        key={i}
                        style={{
                          padding: '6px 8px',
                          margin: '6px 0',
                          borderBottom: '1px solid #ccc',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          boxShadow: 'inset 0 0 2px rgba(0,0,0,0.1)',
                        }}
                      >
                        {line}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <Routes>
                <Route path="/" element={<div>Select a view above.</div>} />
                <Route path="/frameworks" element={<FrameworkManager />} />
              </Routes>
              {toast && (
                <div style={{
                  position: 'fixed',
                  bottom: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#222',
                  color: '#fff',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  zIndex: 1000
                }}>
                  {toast}
                </div>
              )}
            </div>
          </Router>
        </>
      )}
    </>
  );
}


export default App;